import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Button, CircularProgress, IconButton, Link, ListItemIcon, ListItemText, Menu, MenuItem, Snackbar, TextField, Tooltip, Typography } from '@mui/material';
import Alert from '@mui/material/Alert';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SendIcon from '@mui/icons-material/Send';
import TableChartIcon from '@mui/icons-material/TableChart';
import styled from '@emotion/styled';
import { askAiQuestion, exportAiConversation, getAiAnswer, getAiConversation } from '../redux/ducks/ai';
import { useSocketEvent } from '../hooks/useSocketEvent';
import { useBrandName } from '../utils/brandUtils';
import { AIMarkdown } from './AIResponseDialog';

export const MAX_QUESTION_LENGTH = 2000;
const POLL_INTERVAL_MS = 6000;
const CONVERSATION_STORAGE_KEY = 'aiAskConversationId';
const PENDING_STORAGE_KEY = 'aiAskPendingContextId';


const PENDING_COPY = 'Working on it — a question about your data can take a minute.';
const SLOW_COPY = 'Still working. This one is taking longer than usual.';
const TIMED_OUT_COPY = 'No answer came back in time. The assistant may be unavailable — try asking again.';
// Past this the request is treated as lost. Matches the server's pending TTL, so the spinner can
// never outlive the record it is waiting on.
const ANSWER_TIMEOUT_MS = 30 * 60 * 1000;
const SLOW_AFTER_MS = 90 * 1000;
const FAILED_COPY = 'The AI could not answer that. Try rephrasing your question.';
const EXPIRED_COPY = 'That answer is no longer available. Ask the question again to retry.';
const SEND_FAILED_COPY = 'The question could not be sent.';
const EXPORT_FAILED_COPY = 'The export could not be created.';

const EXAMPLE_QUESTIONS = [
    'How many projects were created last month?',
    'Which accounts logged the most time last week?',
    'Which projects have no time logged against them this month?',
];

// A pipe row followed by a dashed separator row is the only thing the server will find to put in a
// spreadsheet, so the button offers itself on exactly the same condition.
const MARKDOWN_TABLE = /^\s*\|.*\|\s*\r?\n\s*\|[\s:|-]*-[\s:|-]*\|\s*$/m;

const readStored = (key) => {
    try {
        return window.sessionStorage.getItem(key) || '';
    } catch (e) {
        return '';
    }
};

const writeStored = (key, value) => {
    try {
        if (value) window.sessionStorage.setItem(key, value);
        else window.sessionStorage.removeItem(key);
    } catch (e) {
        return;
    }
};

// The pending marker holds the start time as well as the id: both surfaces read this on mount, and
// stamping a fresh Date.now() there restarted the elapsed counter — and the timeout with it — every
// time the question moved between the panel and the page.
const readPending = () => {
    const raw = readStored(PENDING_STORAGE_KEY);
    if (!raw) return null;
    const [contextId, startedAt] = raw.split('|');
    if (!contextId) return null;
    const parsed = Number(startedAt);
    return { contextId, startedAt: Number.isFinite(parsed) && parsed > 0 ? parsed : Date.now() };
};

const writePending = (contextId, startedAt) => {
    writeStored(PENDING_STORAGE_KEY, contextId ? `${contextId}|${startedAt || Date.now()}` : '');
};

const Root = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    height: '100%',
}));

const Header = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 1, 1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
}));

const HeaderText = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    flexGrow: 1,
}));

const HeaderTitle = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.base,
    fontWeight: theme.typography.fontWeightSemiBold,
    lineHeight: 1.2,
}));

const HeaderSubtitle = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
    lineHeight: 1.3,
}));

const Transcript = styled(Box)(({ theme }) => ({
    flexGrow: 1,
    overflowY: 'auto',
    // Reserved up front: letting the scrollbar appear with the first long answer narrows the
    // transcript but not the composer below it, so the two visibly stop lining up.
    scrollbarGutter: 'stable',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    minHeight: 0,
}));

const TranscriptInner = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    width: '100%',
}));

const EmptyState = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
}));

const ExampleButton = styled(Box)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1, 1.5),
    fontSize: theme.typography.size.md,
    cursor: 'pointer',
    color: theme.palette.text.primary,
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));

const Bubble = styled(Box, { shouldForwardProp: (prop) => prop !== 'variant' })(({ theme, variant }) => ({
    maxWidth: variant === 'user' ? '85%' : '100%',
    alignSelf: variant === 'user' ? 'flex-end' : 'flex-start',
    padding: theme.spacing(1, 1.5),
    borderRadius: theme.shape.borderRadius * 2,
    fontSize: theme.typography.size.md,
    wordBreak: 'break-word',
    backgroundColor:
        variant === 'user' ? theme.palette.primary.main : variant === 'failed' ? theme.palette.warning.light : theme.palette.action.hover,
    color: variant === 'user' ? theme.palette.primary.contrastText : variant === 'failed' ? theme.palette.warning.contrastText : theme.palette.text.primary,
    width: variant === 'answer' ? '100%' : 'auto',
}));

const PendingRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

const Composer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderTop: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
}));

const ComposerInner = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    width: '100%',
}));

const AskAIConversation = ({ variant = 'panel', onClose, onExpand }) => {
    const dispatch = useDispatch();
    const brandName = useBrandName();
    const isPage = variant === 'page';

    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState('');
    const [exportAnchor, setExportAnchor] = useState(null);
    const [exporting, setExporting] = useState('');
    const [toast, setToast] = useState(null);

    const conversationIdRef = useRef('');
    const transcriptRef = useRef(null);

    const pendingContextIds = useMemo(
        () =>
            messages
                .filter((message) => message.role === 'assistant' && message.status === 'pending' && message.contextId)
                .map((message) => message.contextId),
        [messages],
    );
    const pendingKey = pendingContextIds.join(',');
    const isWaiting = messages.some((message) => message.role === 'assistant' && message.status === 'pending');

    const answers = useMemo(() => messages.filter((message) => message.role === 'assistant' && message.status === 'ready' && message.content), [messages]);
    const hasAnswer = answers.length > 0;
    const hasTable = useMemo(() => answers.some((message) => MARKDOWN_TABLE.test(message.content)), [answers]);

    const [elapsedTick, setElapsedTick] = useState(Date.now());

    // Nothing else stops a pending bubble when the backend never answers — the poll just keeps
    // getting 200/pending until the record expires, which reads as a spinner that never ends.
    useEffect(() => {
        if (!isWaiting) return undefined;
        const tick = setInterval(() => {
            const now = Date.now();
            setElapsedTick(now);
            setMessages((previous) =>
                previous.map((message) =>
                    message.role === 'assistant' && message.status === 'pending' && message.startedAt && now - message.startedAt > ANSWER_TIMEOUT_MS
                        ? { ...message, status: 'failed', content: TIMED_OUT_COPY }
                        : message,
                ),
            );
        }, 1000);
        return () => clearInterval(tick);
    }, [isWaiting]);

    const settleMessage = useCallback((contextId, patch) => {
        setMessages((previous) => previous.map((message) => (message.contextId === contextId ? { ...message, ...patch } : message)));
        if (readPending()?.contextId === contextId) writePending('');
    }, []);

    const resolveAnswer = useCallback(
        (contextId) => {
            dispatch(
                getAiAnswer(contextId, (response) => {
                    if (!response?.ok) {
                        if (response?.notFound) settleMessage(contextId, { status: 'failed', content: EXPIRED_COPY });
                        return;
                    }
                    const record = response.record;
                    if (!record || record.status === 'pending') return;
                    if (record.status === 'ready') {
                        settleMessage(contextId, { status: 'ready', content: record.content || '' });
                    } else {
                        settleMessage(contextId, { status: 'failed', content: record.error ? `The AI could not answer that: ${record.error}` : FAILED_COPY });
                    }
                }),
            );
        },
        [dispatch, settleMessage],
    );

    useEffect(() => {
        const storedConversationId = readStored(CONVERSATION_STORAGE_KEY);
        if (!storedConversationId) return;
        conversationIdRef.current = storedConversationId;
        dispatch(
            getAiConversation(storedConversationId, (response) => {
                if (!response?.ok) return;
                const turns = response.turns || [];
                if (!turns.length) {
                    conversationIdRef.current = '';
                    writeStored(CONVERSATION_STORAGE_KEY, '');
                    writePending('');
                    return;
                }
                const restored = turns.map((turn, index) => ({
                    id: `turn-${index}`,
                    role: turn.role === 'assistant' ? 'assistant' : 'user',
                    status: turn.role === 'assistant' ? (turn.failed ? 'failed' : 'ready') : undefined,
                    content: turn.content || '',
                }));
                const pending = readPending();
                if (pending && turns[turns.length - 1]?.role === 'user') {
                    restored.push({ id: pending.contextId, role: 'assistant', status: 'pending', contextId: pending.contextId, content: '', startedAt: pending.startedAt });
                } else {
                    writePending('');
                }
                setMessages(restored);
            }),
        );
    }, [dispatch]);

    useEffect(() => {
        if (!pendingKey) return undefined;
        const contextIds = pendingKey.split(',');
        contextIds.forEach(resolveAnswer);
        const interval = setInterval(() => contextIds.forEach(resolveAnswer), POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [pendingKey, resolveAnswer]);

    useSocketEvent('aiResponse:ready', (payload) => {
        if (payload?.kind !== 'question' || !payload?.contextId) return;
        if (!pendingContextIds.includes(payload.contextId)) return;
        resolveAnswer(payload.contextId);
    });

    useEffect(() => {
        if (!transcriptRef.current) return;
        transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }, [messages]);

    const handleSend = useCallback(() => {
        const question = draft.trim();
        if (!question || question.length > MAX_QUESTION_LENGTH || isWaiting) return;
        const startedAt = Date.now();
        const localId = `local-${startedAt}`;
        setDraft('');
        setMessages((previous) => [
            ...previous,
            { id: `${localId}-question`, role: 'user', content: question },
            { id: localId, role: 'assistant', status: 'pending', content: '', startedAt },
        ]);
        dispatch(
            askAiQuestion(question, conversationIdRef.current, (response) => {
                if (!response?.ok) {
                    setMessages((previous) =>
                        previous.map((message) => (message.id === localId ? { ...message, status: 'failed', content: response?.message || SEND_FAILED_COPY } : message)),
                    );
                    return;
                }
                const { conversationId, contextId } = response.data || {};
                if (conversationId) {
                    conversationIdRef.current = conversationId;
                    writeStored(CONVERSATION_STORAGE_KEY, conversationId);
                }
                if (!contextId) {
                    setMessages((previous) => previous.map((message) => (message.id === localId ? { ...message, status: 'failed', content: SEND_FAILED_COPY } : message)));
                    return;
                }
                writePending(contextId, startedAt);
                setMessages((previous) => previous.map((message) => (message.id === localId ? { ...message, contextId } : message)));
            }),
        );
    }, [dispatch, draft, isWaiting]);

    const handleKeyDown = (event) => {
        if (event.key !== 'Enter' || event.shiftKey) return;
        event.preventDefault();
        handleSend();
    };

    const handleNewConversation = () => {
        conversationIdRef.current = '';
        writeStored(CONVERSATION_STORAGE_KEY, '');
        writePending('');
        setMessages([]);
        setDraft('');
    };

    const handleExport = (format) => {
        setExportAnchor(null);
        if (!conversationIdRef.current) return;
        setExporting(format);
        dispatch(
            exportAiConversation(conversationIdRef.current, format, '', (response) => {
                setExporting('');
                if (!response?.ok) {
                    setToast({ severity: 'error', message: response?.message || EXPORT_FAILED_COPY });
                    return;
                }
                setToast({
                    severity: 'success',
                    message: format === 'sheet' ? 'Spreadsheet created in your Google Drive.' : 'Document created in your Google Drive.',
                    file: response.file,
                });
            }),
        );
    };

    const tooLong = draft.length > MAX_QUESTION_LENGTH;
    const sendDisabled = !draft.trim() || tooLong || isWaiting;
    const exportDisabled = !hasAnswer || !!exporting;

    return (
        <Root>
            <Header>
                <AutoAwesome fontSize="small" color="primary" />
                <HeaderText>
                    <HeaderTitle>Ask {brandName}</HeaderTitle>
                    <HeaderSubtitle>Questions about your system data</HeaderSubtitle>
                </HeaderText>

                <Tooltip title={hasAnswer ? 'Save this conversation to your Google Drive' : 'Ask a question first — there is nothing to export yet'}>
                    <span>
                        {isPage ? (
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={exporting ? <CircularProgress size={16} /> : <DriveFileMoveIcon />}
                                disabled={exportDisabled}
                                onClick={(event) => setExportAnchor(event.currentTarget)}
                            >
                                Export
                            </Button>
                        ) : (
                            <IconButton aria-label="Export this conversation to Google Drive" size="small" disabled={exportDisabled} onClick={(event) => setExportAnchor(event.currentTarget)}>
                                {exporting ? <CircularProgress size={16} /> : <DriveFileMoveIcon fontSize="small" />}
                            </IconButton>
                        )}
                    </span>
                </Tooltip>

                <Tooltip title="Start a new conversation">
                    <span>
                        <IconButton aria-label="Start a new conversation" size="small" onClick={handleNewConversation} disabled={!messages.length}>
                            <RestartAltIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>

                {onExpand && (
                    <Tooltip title="Open the full page">
                        <IconButton aria-label="Open the full Ask page" size="small" onClick={onExpand}>
                            <OpenInFullIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}

                {onClose && (
                    <Tooltip title="Close">
                        <IconButton aria-label="Close the question window" size="small" onClick={onClose}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Header>

            <Menu anchorEl={exportAnchor} open={!!exportAnchor} onClose={() => setExportAnchor(null)}>
                <MenuItem onClick={() => handleExport('doc')}>
                    <ListItemIcon>
                        <DescriptionIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Google Doc" secondary="The full conversation, formatted" />
                </MenuItem>
                <Tooltip title={hasTable ? '' : 'This answer has no table in it, so there is nothing to put in a spreadsheet.'} placement="left">
                    <span>
                        <MenuItem onClick={() => handleExport('sheet')} disabled={!hasTable}>
                            <ListItemIcon>
                                <TableChartIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary="Google Sheet" secondary={hasTable ? 'One tab per table in the answer' : 'No table in this answer'} />
                        </MenuItem>
                    </span>
                </Tooltip>
            </Menu>

            <Transcript ref={transcriptRef}>
                <TranscriptInner>
                    {!messages.length && (
                        <EmptyState>
                            <Typography variant="body2">Ask anything about the data in {brandName} — projects, tasks, time, accounts or invoices. Answers are read-only.</Typography>
                            <Typography variant="caption">Try one of these:</Typography>
                            {EXAMPLE_QUESTIONS.map((example) => (
                                <ExampleButton key={example} role="button" tabIndex={0} onClick={() => setDraft(example)}>
                                    {example}
                                </ExampleButton>
                            ))}
                        </EmptyState>
                    )}
                    {messages.map((message) => {
                        if (message.role === 'user') {
                            return (
                                <Bubble key={message.id} variant="user">
                                    {message.content}
                                </Bubble>
                            );
                        }
                        if (message.status === 'pending') {
                            return (
                                <Bubble key={message.id} variant="answer">
                                    <PendingRow>
                                        <CircularProgress size={16} />
                                        <Typography variant="body2">
                                            {(() => {
                                                const seconds = message.startedAt ? Math.max(0, Math.round((elapsedTick - message.startedAt) / 1000)) : 0;
                                                const base = message.startedAt && elapsedTick - message.startedAt > SLOW_AFTER_MS ? SLOW_COPY : PENDING_COPY;
                                                return seconds ? `${base} (${seconds}s)` : base;
                                            })()}
                                        </Typography>
                                    </PendingRow>
                                </Bubble>
                            );
                        }
                        if (message.status === 'failed') {
                            return (
                                <Bubble key={message.id} variant="failed">
                                    <Typography variant="body2">{message.content || FAILED_COPY}</Typography>
                                </Bubble>
                            );
                        }
                        return (
                            <Bubble key={message.id} variant="answer">
                                <AIMarkdown>{message.content}</AIMarkdown>
                            </Bubble>
                        );
                    })}
                </TranscriptInner>
            </Transcript>

            <Composer>
                <ComposerInner>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        maxRows={isPage ? 8 : 4}
                        size="small"
                        label="Your question"
                        placeholder="e.g. how many projects were created last week?"
                        value={draft}
                        onChange={(event) => setDraft(event.target.value)}
                        onKeyDown={handleKeyDown}
                        error={tooLong}
                        helperText={
                            tooLong
                                ? `Questions are limited to ${MAX_QUESTION_LENGTH} characters (${draft.length} used).`
                                : isWaiting
                                    ? 'Waiting for the current answer — send unlocks when it arrives.'
                                    : 'Enter to send, Shift + Enter for a new line.'
                        }
                    />
                    <Tooltip title={isWaiting ? 'Waiting for the current answer' : 'Send question'}>
                        <span>
                            <IconButton aria-label="Send question" color="primary" onClick={handleSend} disabled={sendDisabled}>
                                <SendIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                </ComposerInner>
            </Composer>

            <Snackbar open={!!toast} autoHideDuration={toast?.file ? 12000 : 6000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={toast?.severity || 'info'} onClose={() => setToast(null)} variant="filled">
                    {toast?.message}
                    {toast?.file?.url && (
                        <>
                            {' '}
                            <Link href={toast.file.url} target="_blank" rel="noopener noreferrer" color="inherit" underline="always">
                                Open it
                            </Link>
                        </>
                    )}
                </Alert>
            </Snackbar>
        </Root>
    );
};

export default AskAIConversation;
